import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ClueCard from "@/components/feature/ClueCard";
import { clues } from "@/mocks/clueData";
import { useSeo } from "@/hooks/useSeo";

export default function ClueDetailPage() {
  const { slug } = useParams();
  const clue = clues.find((item) => item.slug === slug);

  useSeo({
    title: clue ? `${clue.title} - 17.cc网友爆料` : "投稿不存在 - 17.cc网友爆料站",
    description: clue
      ? `${clue.excerpt}本文来自17.cc网友投稿，属于${clue.categoryName}主题，欢迎阅读并参与理性讨论。`
      : "你访问的投稿可能已被移动或删除，返回17.cc网友爆料站查看最新内容。",
    keywords: clue
      ? `17.cc网友爆料,${clue.categoryName},${clue.tags.join(",")},线索征集`
      : "17.cc网友爆料",
    path: `/clues/${slug ?? ""}`,
    schema: clue
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: clue.title,
            description: clue.excerpt,
            datePublished: clue.date,
            articleSection: clue.categoryName,
            keywords: clue.tags.join(","),
            author: { "@type": "Person", name: clue.author },
            publisher: { "@type": "Organization", name: "17.cc 网友爆料站" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://17.cc/" },
              { "@type": "ListItem", position: 2, name: "热门爆料", item: "https://17.cc/clues" },
              {
                "@type": "ListItem",
                position: 3,
                name: clue.title,
                item: `https://17.cc/clues/${clue.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!clue) {
    return (
      <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-600">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">没有找到这条投稿</h1>
        <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
          它可能已被移动或删除。你可以返回爆料列表，继续浏览其他网友分享的内容。
        </p>
        <Link
          to="/clues"
          className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回爆料列表
        </Link>
      </div>
    );
  }

  const related = clues
    .filter((item) => item.id !== clue.id && item.categoryId === clue.categoryId)
    .concat(clues.filter((item) => item.id !== clue.id && item.categoryId !== clue.categoryId))
    .slice(0, 3);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumbs items={[{ label: "热门爆料", to: "/clues" }, { label: clue.categoryName }]} />

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <article className="lg:col-span-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
            <i className="ri-price-tag-3-line"></i>
            {clue.categoryName}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-4xl">
            {clue.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {clue.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {clue.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              阅读约 {clue.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {clue.views.toLocaleString()} 次浏览
            </span>
          </div>

          <div className="mt-7 h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
            <img src={clue.image} alt={clue.imageAlt} className="h-full w-full object-cover object-top" />
          </div>

          <div className="clue-prose mt-8 text-[15px] leading-8 text-foreground-700">
            {clue.content.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {clue.quote && (
            <blockquote className="mt-6 rounded-lg border-l-4 border-accent-500 bg-accent-50 px-5 py-4">
              <p className="font-heading text-base font-semibold leading-7 text-foreground-900">
                “{clue.quote}”
              </p>
            </blockquote>
          )}

          <div className="mt-8 flex flex-wrap gap-2">
            {clue.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-information-line text-lg text-primary-600"></i>
              关于这篇投稿
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              本文内容来自网友投稿，仅代表投稿人个人经历与观点。文中涉及的具体人物与机构信息已做模糊处理。
              如你认为内容存在事实偏差或涉及隐私，可通过联系页面提交说明，我们会在核实后追加更正。
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/submit"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line"></i>
                我也要投稿
              </Link>
              <Link
                to="/clues"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200"
              >
                <i className="ri-arrow-left-line"></i>
                返回列表
              </Link>
            </div>
          </div>
        </article>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">投稿人信息</h2>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-user-3-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{clue.author}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">已通过隐私保护处理</p>
                </div>
              </div>
              <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-background-200 pt-4 text-center">
                <div>
                  <dt className="text-xs text-foreground-500">点赞</dt>
                  <dd className="mt-1 font-heading text-base font-bold text-foreground-950">
                    {clue.likes}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">评论</dt>
                  <dd className="mt-1 font-heading text-base font-bold text-foreground-950">
                    {clue.comments}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">浏览</dt>
                  <dd className="mt-1 font-heading text-base font-bold text-foreground-950">
                    {(clue.views / 1000).toFixed(1)}k
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-alert-line text-lg text-secondary-700"></i>
                理性参与讨论
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                请勿在评论区发布他人隐私信息或未经证实的指控。如果你的经历与本文相关，欢迎用投稿的方式补充细节。
              </p>
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-16 border-t border-background-200 pt-10">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((item) => (
            <ClueCard key={item.id} clue={item} />
          ))}
        </div>
      </section>
    </div>
  );
}